import { useLazyQuery, useMutation } from "@apollo/client/react";
import { Box, Button, DialogContent, IconButton, Typography, useMediaQuery } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { styled, useTheme, Theme } from "@mui/material/styles";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { Close } from "@mui/icons-material";
import CreateIcon from "@mui/icons-material/Create";
import certificationIcon from "assets/icons/icon-certification.svg";
import completionIcon from "assets/icons/icon-course.svg";
import pathwayIcon from "assets/icons/icon-pathway.svg";
import {
  CertificationIcon,
  ConfirmDialog,
  ContentBanner,
  Dialog,
  NewIcon,
  Rating,
  RenderHTML,
  Select,
  ShareIcon,
} from "components";
import { ChannelIcon, PathwayIcon, PlayListIcon } from "components/common-icons/common-icons";
import { useFocusStyles } from "hooks/focusBorder";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState, useAppDispatch } from "store";
import {
  KEY_CODE,
  SORT_ALPHABETICAL_ASCENDING,
  SORT_ALPHABETICAL_DESCENDING,
  SORT_DURATION_ASCENDING,
  SORT_DURATION_DESCENDING,
  SORT_PRICE_ASCENDING,
  SORT_PRICE_DESCENDING,
} from "utils/constants";
import * as CanChangeCompletedType from "utils/graphql/CanChangeCompletedStatusToEnroll";
import { mobileDateString } from "utils/helpers";
import portalSettingsManager from "utils/portalSettingsManager";
import {
  CAN_CHANGE_COMPLETE_TO_ENROLL,
  COMPLETE_CHANNEL_ENROLLMENT,
  COMPLETE_CHANNEL_UNENROLLMENT,
  COMPLETE_PATHWAY_UNENROLLMENT,
  DELETE_PLAYLIST,
} from "utils/queries";
import { PathwayType, SortType } from "utils/types";

const useStyles = makeStyles({
  viewLess: {
    display: "-webkit-box",
    "-webkit-line-clamp": 2,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
});

const BorderLinearProgress = withStyles((theme: Theme) =>
  createStyles({
    root: {
      height: 5,
    },
    colorPrimary: {
      backgroundColor: "#DBF3FF",
    },
    bar: {
      borderRadius: 5,
      backgroundColor: theme.palette.primary.main,
    },
  }),
)(LinearProgress);

const MatrixBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "bg" && prop !== "mobile",
})<{ bg: string; mobile?: boolean }>(({ theme, bg, mobile }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "column",
  textAlign: "center",
  margin: mobile ? "" : "0 25px",
  marginRight: mobile ? "5px" : undefined,
  width: mobile ? "30px" : undefined,
  height: mobile ? "30px" : undefined,
  "&:before": {
    position: "absolute",
    width: "100%",
    height: "40px",
    top: mobile ? -10 : 0,
    left: 0,
    bottom: 0,
    right: 0,
    content: `""`,
    backgroundImage: `url(${bg})`,
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    opacity: 0.2,
  },
  [theme.breakpoints.down("sm")]: {
    display: mobile ? "flex" : "none",
  },
}));

const Matrix = ({
  bg,
  label,
  count = 0,
  mobile = false,
  completedCount = 0,
  pageType,
}: {
  bg: string;
  label?: string;
  count: number;
  mobile?: boolean;
  completedCount?: number;
  pageType: string;
}): React.ReactElement => {
  const theme = useTheme();

  return (
    <MatrixBox bg={bg} mobile={mobile} {...({ component: "li" } as any)}>
      <Typography
        style={{
          fontSize: mobile ? "15px" : "26px",
          fontWeight: "bold",
          color: theme.palette.grey["800"],
          lineHeight: "31px",
          margin: mobile ? "2px 0px 0px 0px" : "5px 0 9px 0",
        }}
      >
        {pageType === "parentpathway" || pageType === "pathway"
          ? `${completedCount}/${count}`
          : count}
      </Typography>
      {label && (
        <Typography
          style={{ fontSize: "11px", color: theme.palette.grey["800"], lineHeight: "16px" }}
        >
          {label}
        </Typography>
      )}
    </MatrixBox>
  );
};

export const PathwayContentHeader = ({
  id,
  title,
  type,
  tags,
  editPlaylist,
  contentMetrics,
  hasCertificate,
  isEnrolled,
  percentageComplete,
  selectSort,
  rating,
  numberOfPeopleWhoRated = 0,
  handleSharContentHeader,
  openRating,
  handleRefetch,
  handleAddToLearning,
  isNew,
  description,
  isAutoEnrolled,
  canEnrollToPathway,
  canUnEnrollFromPathway,
}: {
  type: PathwayType;
  isEnrolled?: boolean;
  handleRefetch?: () => void;
  editPlaylist?: () => void;
  [x: string]: any;
  canEnrollToPathway?: boolean;
  canUnEnrollFromPathway?: boolean;
}): React.ReactElement => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isInMyLearning, setIsInMyLearning] = useState<boolean | null>();
  const [showConfirmDialog, setConfirmDialog] = useState<any>(false);
  const [showDialog, setShowDialog] = useState<any>(false);
  const [confirmDialogTitle, setConfirmDialogTitle] = useState<string>("");
  const [confirmDialogMessage, setConfirmDialogMessage] = useState<string>("");
  const navigate = useNavigate();

  const [completePathwayUnEnrollment] = useMutation(COMPLETE_PATHWAY_UNENROLLMENT);
  const [deletePlaylist] = useMutation(DELETE_PLAYLIST);
  const [completeChannelEnrollment] = useMutation(COMPLETE_CHANNEL_ENROLLMENT);
  const [completeChannelUnEnrollment] = useMutation(COMPLETE_CHANNEL_UNENROLLMENT);
  const searchSort = useSelector((state: RootState) => state.core.searchSort);
  const [sort, setSort] = useState<SortType>(searchSort);
  const dispatch = useAppDispatch();
  const handleSort = (id: any) => {
    setSort(id);
    selectSort(id);
  };

  const [showMore, setShowMore] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const elementRef = descriptionRef.current as HTMLElement;
    if (elementRef && elementRef.clientHeight < elementRef.scrollHeight) {
      setShowMore(true);
    } else {
      setShowMore(false);
    }
  }, [descriptionRef.current]);

  const [canChangeCompletedStatus, { data }] =
    useLazyQuery<CanChangeCompletedType.CanChangeCompletedStatusToEnroll>(
      CAN_CHANGE_COMPLETE_TO_ENROLL,
    );

  const handleAddtoMyLearning = () => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: true },
    });
    canChangeCompletedStatus({ variables: { pathwayId: id } });
  };

  const classes = useStyles();

  useEffect(() => {
    if (data) {
      dispatch({
        type: "loader/showandhide",
        payload: { show: false },
      });
      if (data.canChangeCompletedStatusToEnroll) {
        setConfirmDialogTitle(t("confirmdialogtitle"));
        setConfirmDialogMessage(t("pathway_dialog"));
        setConfirmDialog(true);
      } else {
        handleAddToLearning();
      }
    }
  }, [data]);

  const Icon = withStyles((theme: Theme) => ({
    root: {
      // backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      borderRadius: "50%",
      width: 14,
      height: 14,
      cursor: "pointer",
      position: "absolute",
      padding: "2px",
      "& path": { fill: portalSettingsManager.application?.common?.themeColor || "#0091da" },
    },
  }))(CreateIcon);

  const handleConfirmDialogClick = () => {
    if (type === PathwayType.pathway) {
      handleAddToLearning();
    } else if (type === PathwayType.playlist) {
      handleRemovePlaylist();
    }
  };
  const handleRemovePlaylist = () => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: true, message: t("delete_playlist_msg") },
    });

    deletePlaylist({ variables: { playlistId: id } })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("delete_playlist_msg") },
        });
        navigate("/");
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("playlist"),
            message: t("playlist_deletion_progress"),
          },
        });
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("remove_from_mylearning") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("playlist"),
            message: t("playlist_deletion_error"),
          },
        });
      });
  };

  const handlePathwayUnEnrollment = () => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: true, message: t("remove_from_mylearning") },
    });

    completePathwayUnEnrollment({ variables: { pathwayId: id } })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("remove_from_mylearning") },
        });
        setIsInMyLearning(false);
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: pageType === "pathway" ? t("pathway") : t("parent_pathway"),
            message:
              pageType === "pathway"
                ? t("pathway_removal_dialog")
                : t("parent_pathway_removal_dialog"),
          },
        });
        handleRefetch?.();
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("remove_from_mylearning") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("pathway"),
            message: t("pathway_unenrollment_error"),
          },
        });
      });
  };

  const handleChannelUnEnrollment = () => {
    dispatch({ type: "loader/showandhide", payload: { show: true, message: t("unsubscribing ") } });
    completeChannelUnEnrollment({ variables: { channelId: id } })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("unsubscribing ") },
        });
        setIsInMyLearning(false);
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("channel"),
            message: t("channel_unsubscribed_successfully"),
          },
        });
        handleRefetch?.();
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("unsubscribing ") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("channel"),
            message: t("channel_unenrollment_error"),
          },
        });
      });
  };

  const handleChannelEnrollment = () => {
    dispatch({ type: "loader/showandhide", payload: { show: true, message: t("subscribing ") } });
    completeChannelEnrollment({ variables: { channelId: id } })
      .then(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("subscribing ") },
        });
        setIsInMyLearning(true);
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("channel"),
            message: t("channel_subscribed_successfully"),
          },
        });
        handleRefetch?.();
      })
      .catch(() => {
        dispatch({
          type: "loader/showandhide",
          payload: { show: false, message: t("subscribing ") },
        });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("channel"),
            message: t("channel_subscribed_error"),
          },
        });
      });
  };

  useEffect(() => {
    setIsInMyLearning(isEnrolled);
  }, [isEnrolled]);

  const handleDeletePlaylist = () => {
    setConfirmDialogTitle(t("playlist_delete"));
    setConfirmDialogMessage(t("confirm_playlist_deletion"));
    setConfirmDialog(true);
  };

  const focusClass = useFocusStyles();

  const showNewIcon = isNew && type !== "playlist";
  const location = useLocation();
  const pageType = location.pathname.toLowerCase().startsWith("/pathway/")
    ? PathwayType.pathway
    : location.pathname.toLowerCase().startsWith("/channel/")
      ? PathwayType.channel
      : location.pathname.toLowerCase().startsWith("/playlist/")
        ? PathwayType.playlist
        : PathwayType.parentpathway;

  return (
    <>
      {showNewIcon && (
        <Box width="5%" style={{ marginBottom: "5px" }}>
          <NewIcon style={{ position: "static", padding: "4px 9px 3px 7px" }} />
        </Box>
      )}
      <Box display={isMobile ? "block" : "flex"} justifyContent="space-between">
        {isMobile && (
          <Box display="flex" alignItems="center" justifyContent="space-between" marginTop="5px">
            <Box display="flex" alignItems="center">
              {(() => {
                const settings = { width: "19px", height: "17px", stroke: "#000000" };
                switch (type) {
                  case PathwayType.pathway: {
                    return <PathwayIcon {...settings} />;
                  }
                  case PathwayType.playlist: {
                    return <PlayListIcon {...settings} />;
                  }
                  case PathwayType.channel: {
                    return <ChannelIcon {...settings} />;
                  }
                }
              })()}
              <Typography
                style={{
                  fontWeight: "bold",
                  fontSize: "12px",
                  letterSpacing: "2.4px",
                  color: theme.palette.grey["800"],
                  fontFamily: "Arial",
                  lineHeight: "14px",
                }}
              >
                {type.toUpperCase()}
              </Typography>
            </Box>
            <Box display="flex">
              {portalSettingsManager.application?.features?.allowShareContent &&
                type !== PathwayType.playlist && (
                  <Box display="flex">
                    <Box
                      style={{
                        display: "inline-block",
                        border: "solid",
                        borderRadius: "50%",
                        borderWidth: "1px",
                        borderColor: theme.palette.grey["A100"],
                        aspectRatio: "1",
                        width: "30px",
                        height: "30px",
                      }}
                    >
                      <IconButton onClick={handleSharContentHeader} size="large">
                        <ShareIcon
                          stroke={theme.palette.grey["A100"]}
                          style={{ transform: "translate(-15%, -15%)" }}
                          itemName={type}
                        />
                      </IconButton>
                    </Box>
                    {type === PathwayType.pathway && hasCertificate && isMobile && (
                      <Box marginLeft="8px" position="relative" width="30px">
                        <CertificationIcon
                          stroke={theme.palette.grey["A100"]}
                          style={{
                            position: "absolute",
                            top: "-3px",
                            width: "30px",
                            height: "40px",
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}
              {type === PathwayType.playlist && hasCertificate && (
                <Box marginLeft="13px">
                  <CertificationIcon
                    width="22px"
                    height="30px"
                    stroke={theme.palette.grey["A100"]}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}
        <Box width={isMobile ? "100%" : "50%"}>
          <Box display="flex">
            <Typography
              style={{
                display: "inline",
                fontSize: isMobile ? "18px" : "26px",
                fontWeight: isMobile ? "bold" : undefined,
                color: theme.palette.grey["800"],
                position: "relative",
              }}
              component="h2"
            >
              {title}
            </Typography>
            <Typography
              style={{
                display: "inline",
                fontSize: isMobile ? "18px" : "26px",
                fontWeight: isMobile ? "bold" : undefined,
                maxWidth: isMobile ? "15px" : undefined,
                color: theme.palette.grey["800"],
                position: "relative",
              }}
            >
              {type === PathwayType.playlist && !isMobile && (
                <IconButton
                  aria-label={`${t("aria_label_click_to_edit")} ${t(title)}`}
                  onClick={editPlaylist}
                  style={{
                    borderColor: portalSettingsManager.application?.common?.themeColor,
                    marginLeft: "5px",
                  }}
                  tabIndex={0}
                  className={`${focusClass.borderItem} ${focusClass.focusItem}`}
                  size="large"
                >
                  <Icon onClick={editPlaylist} />
                </IconButton>
              )}
            </Typography>
            {type === PathwayType.playlist && isMobile && (
              <IconButton onClick={editPlaylist} aria-label={t("edit_playlist")} size="large">
                <Icon />
              </IconButton>
            )}
          </Box>
          {isMobile && (
            <Box
              display="flex"
              justifyContent="space-evenly"
              component="ul"
              style={{ listStyle: "none" }}
              marginLeft="-1.5rem"
            >
              {pageType === PathwayType.parentpathway &&
                contentMetrics?.numberOfCertifications !== 0 && (
                  <Matrix
                    mobile
                    pageType={pageType}
                    bg={certificationIcon}
                    label="Certifications"
                    count={contentMetrics?.numberOfCertifications}
                    completedCount={contentMetrics?.numberOfCertificationsCompleted}
                  />
                )}
              <Matrix
                mobile
                label={t("Courses")}
                bg={completionIcon}
                count={contentMetrics?.numberOfCourses}
                completedCount={contentMetrics?.numberOfCoursesCompleted}
                pageType={pageType}
              />
              {type !== PathwayType.playlist && type !== PathwayType.pathway && (
                <Matrix
                  mobile
                  label={t("pathways")}
                  bg={pathwayIcon}
                  count={contentMetrics?.numberOfPathways}
                  completedCount={contentMetrics?.numberOfPathwaysCompleted}
                  pageType={pageType}
                />
              )}
            </Box>
          )}
          {description && (
            <div
              style={{
                display: isMobile ? "block" : "flex",
                margin: isMobile ? "0px" : "10px 0 30px 0",
              }}
            >
              <div
                className={classes.viewLess}
                style={{ fontSize: "13px", color: theme.palette.grey["800"], lineHeight: "21px" }}
                ref={descriptionRef}
              >
                <RenderHTML content={description ?? ""} />{" "}
              </div>
              {showMore && (
                <Box
                  style={{
                    minWidth: "100px",
                    alignSelf: "flex-end",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "13px",
                    color: theme.palette.grey["800"],
                    lineHeight: "21px",
                  }}
                  onClick={() => setShowDialog(true)}
                  onKeyDown={(e) => {
                    if (e.key.toUpperCase() === KEY_CODE.ENTER) {
                      setShowDialog(true);
                    }
                  }}
                  aria-label={t("view_more")}
                  role="button"
                  tabIndex={0}
                >
                  {t("view_more")}
                </Box>
              )}
            </div>
          )}

          <Box display="flex" flexDirection="row" alignItems="center">
            <Box marginLeft={"10px"}>
              {portalSettingsManager.application?.features?.enableContentRating &&
                type !== PathwayType.playlist && (
                  <Rating
                    score={rating || 0}
                    numberOfReviews={numberOfPeopleWhoRated}
                    onSubmit={openRating}
                    display={true}
                  />
                )}
            </Box>
          </Box>
          <Box display="flex" flexWrap="wrap" role="list">
            {(tags || []).map((item: any, index: number) =>
              item ? (
                <span
                  key={index}
                  aria-label={item}
                  role="listitem"
                  style={{
                    fontSize: "11px",
                    color: theme.palette.grey["800"],
                    marginRight: "7px",
                    marginBottom: "7px",
                    padding: "3px 15px",
                    background: "#f5f5f5",
                  }}
                >
                  {item}
                </span>
              ) : (
                <></>
              ),
            )}
          </Box>
        </Box>
        {isMobile && (
          <>
            {(type === PathwayType.pathway || type === PathwayType.parentpathway) &&
              isInMyLearning && (
                <>
                  <Box style={{ marginTop: ".4rem", marginBottom: ".5rem" }}>
                    {pageType === PathwayType.parentpathway
                      ? t("parent_pathway_progress")
                      : t("pathway_progress")}
                    <span style={{ marginLeft: ".5rem" }}>{percentageComplete}%</span>
                  </Box>
                  <Box></Box>
                  <ContentBanner
                    showProgress
                    item={{
                      percentageComplete,
                      isEnrolled: !!isEnrolled,
                      statusCode: "InProgress",
                    }}
                  />
                </>
              )}
            {type === PathwayType.channel && isInMyLearning && (
              <>
                <ContentBanner
                  item={{
                    percentageComplete,
                    isEnrolled: !!isEnrolled,
                    statusCode: "InProgress",
                  }}
                >
                  {t("subscribed")}
                </ContentBanner>
              </>
            )}
          </>
        )}
        <Box
          display={{ xs: "none", sm: "flex" }}
          alignItems="center"
          alignSelf="flex-start"
          marginTop={showNewIcon ? "-25px" : "5px"}
          component="ul"
          style={{ listStyle: "none" }}
        >
          {pageType !== "parentpathway" && (
            <Matrix
              bg={completionIcon}
              label="Courses"
              count={contentMetrics?.numberOfCourses}
              completedCount={contentMetrics?.numberOfCoursesCompleted}
              pageType={pageType}
            />
          )}
          {type !== PathwayType.playlist &&
            type !== PathwayType.pathway &&
            pageType !== "parentpathway" && (
              <Matrix
                bg={pathwayIcon}
                label="Pathways"
                count={contentMetrics?.numberOfPathways}
                pageType={pageType}
              />
            )}
          {type === PathwayType.pathway &&
            pageType === "pathway" &&
            hasCertificate &&
            !isMobile && (
              <Box marginTop="-5px" component="li">
                <CertificationIcon width="54px" height="64px" stroke={theme.palette.grey["A100"]} />
              </Box>
            )}
          {type === PathwayType.playlist && hasCertificate && !isMobile && (
            <Box marginTop="-5px" component="li">
              <CertificationIcon width="54px" height="64px" stroke={theme.palette.grey["A100"]} />
            </Box>
          )}
          {pageType === "parentpathway" && !isMobile && (
            <>
              {contentMetrics?.numberOfCertifications !== 0 && (
                <Matrix
                  pageType={pageType}
                  bg={certificationIcon}
                  label="Certifications"
                  count={contentMetrics?.numberOfCertifications}
                  completedCount={contentMetrics?.numberOfCertificationsCompleted}
                />
              )}
              <Matrix
                pageType={pageType}
                bg={pathwayIcon}
                label="Pathways"
                count={contentMetrics?.numberOfPathways}
                completedCount={contentMetrics?.numberOfPathwaysCompleted}
              />
              <Matrix
                pageType={pageType}
                bg={completionIcon}
                label="Courses"
                count={contentMetrics?.numberOfCourses}
                completedCount={contentMetrics?.numberOfCoursesCompleted}
              />
            </>
          )}
        </Box>
      </Box>
      {showDialog && (
        <Box
          display="flex"
          justifyContent="space-between"
          borderBottom="1px solid  #D8D8D8;"
          paddingBottom="20px"
        >
          <Dialog
            id="pathwaycontentdialog"
            onClose={() => setShowDialog(false)}
            aria-labelledby="adding-playlist-dialog"
            open={true}
            minWidth={0}
            showCloseIcon
            minHeight={isMobile ? 0 : 150}
          >
            <DialogContent style={{ padding: "8px 35px", minWidth: isMobile ? 0 : 360 }}>
              <Typography
                style={{
                  fontSize: "12px",
                  color: theme.palette.grey["800"],
                  lineHeight: "16px",
                  marginTop: "10px",
                }}
              >
                <RenderHTML content={description ?? ""} />
              </Typography>
            </DialogContent>
          </Dialog>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="space-between"
        borderBottom="1px solid  #D8D8D8;"
        paddingBottom="20px"
        flexDirection={isMobile ? "column" : "row"}
      >
        <Box>
          {type === PathwayType.playlist && (
            <Button
              style={{
                borderRadius: 0,
                color:
                  portalSettingsManager.buttonColors?.normal?.active?.text ||
                  theme.palette.grey["800"],
                backgroundColor: portalSettingsManager.buttonColors?.normal?.active?.back,
                padding: "10px 30px",
                fontSize: "12px",
                fontWeight: 700,
                margin: "15px 10px 0px 0px",
                borderColor:
                  portalSettingsManager.buttonColors?.normal?.active?.border ||
                  theme.palette.grey["A100"],
              }}
              // title={t('delete_playlist_button_title')}
              onClick={() => handleDeletePlaylist()}
              startIcon={<Close fontSize="large" />}
              tabIndex={0}
              className={`${focusClass.greyBorder} ${focusClass.focusItem} ${focusClass.secondaryHover}`}
              fullWidth={isMobile ? true : false}
            >
              {t("delete_this_playlist")}
            </Button>
          )}
          {type === PathwayType.pathway && canUnEnrollFromPathway && (
            <Button
              style={{
                border: `1px solid ${
                  portalSettingsManager.buttonColors?.normal?.active?.border ||
                  theme.palette.grey["A100"]
                }`,
                borderRadius: 0,
                color:
                  portalSettingsManager.buttonColors?.normal?.active?.text ||
                  theme.palette.grey["800"],
                backgroundColor: portalSettingsManager.buttonColors?.normal?.active?.back,
                padding: "10px 20px",
                fontSize: "12px",
                fontWeight: 700,
                margin: "15px 10px 0px 0px",
              }}
              onClick={handlePathwayUnEnrollment}
              className={`${focusClass.secondaryHover} ${focusClass.focusItem}`}
              fullWidth={isMobile ? true : false}
            >
              {t("remove_from_mylearning")}
            </Button>
          )}
          {type === PathwayType.pathway && canEnrollToPathway && (
            <Button
              style={{
                border: `1px solid ${
                  portalSettingsManager.buttonColors?.normal?.active?.border ||
                  theme.palette.grey["A100"]
                }`,
                borderRadius: 0,
                color:
                  portalSettingsManager.buttonColors?.normal?.active?.text ||
                  theme.palette.grey["800"],
                backgroundColor: portalSettingsManager.buttonColors?.normal?.active?.back,
                padding: "10px 30px",
                fontSize: "12px",
                fontWeight: 700,
                margin: "15px 10px 0px 0px",
              }}
              className={`${focusClass.secondaryHover} ${focusClass.focusItem}`}
              onClick={handleAddtoMyLearning}
              fullWidth={isMobile ? true : false}
            >
              {t("add_content_to_mylearning").toUpperCase()}
            </Button>
          )}
          {isInMyLearning && type === PathwayType.channel && !isAutoEnrolled && (
            <Button
              style={{
                border: `1px solid ${
                  portalSettingsManager.buttonColors?.normal?.active?.border ||
                  theme.palette.grey["A100"]
                }`,
                borderRadius: 0,
                color:
                  portalSettingsManager.buttonColors?.normal?.active?.text ||
                  theme.palette.grey["800"],
                backgroundColor:
                  portalSettingsManager.buttonColors?.normal?.active?.back || "black",
                padding: "10px 30px",
                fontSize: "12px",
                fontWeight: 700,
                margin: "15px 10px 0px 0px",
              }}
              className={`${focusClass.secondaryHover} ${focusClass.focusItem}`}
              onClick={handleChannelUnEnrollment}
              fullWidth={isMobile ? true : false}
            >
              {t("unsubscribe")}
            </Button>
          )}
          {isInMyLearning === false && type === PathwayType.channel && (
            <Button
              style={{
                border: `1px solid ${
                  portalSettingsManager.buttonColors?.normal?.active?.border ||
                  theme.palette.grey["A100"]
                }`,
                borderRadius: 0,
                color:
                  portalSettingsManager.buttonColors?.normal?.active?.text ||
                  theme.palette.grey["800"],
                backgroundColor:
                  portalSettingsManager.buttonColors?.normal?.active?.back || "black",
                padding: "10px 30px",
                fontSize: "12px",
                fontWeight: 700,
                margin: "15px 10px 0px 0px",
              }}
              className={`${focusClass.secondaryHover} ${focusClass.focusItem}`}
              onClick={handleChannelEnrollment}
              fullWidth={isMobile ? true : false}
            >
              {t("subscribe")}
            </Button>
          )}
          {portalSettingsManager.application?.features?.allowShareContent &&
            type !== PathwayType.playlist &&
            !isMobile && (
              <Button
                style={{
                  border: `1px solid ${
                    portalSettingsManager.buttonColors?.normal?.active?.border ||
                    theme.palette.grey["A100"]
                  }`,
                  borderRadius: 0,
                  color:
                    portalSettingsManager.buttonColors?.normal?.active?.text ||
                    theme.palette.grey["800"],
                  backgroundColor: portalSettingsManager.buttonColors?.normal?.active?.back,
                  padding: "10px 30px",
                  fontSize: "12px",
                  fontWeight: 700,
                  margin: "15px 10px 0px 0px",
                }}
                className={`${focusClass.secondaryHover} ${focusClass.focusItem}`}
                onClick={handleSharContentHeader}
                startIcon={
                  <ShareIcon
                    stroke={portalSettingsManager.buttonColors?.normal?.active?.text || "#000000"}
                    style={{ transform: "translate(20%, 25%)" }}
                    itemName={type}
                  />
                }
              >
                {t("share")}
              </Button>
            )}
        </Box>
        {(type === PathwayType.pathway || type === PathwayType.parentpathway) &&
          isInMyLearning &&
          !isMobile && (
            <Box style={{ minWidth: "300px", marginTop: "9px" }}>
              <Box
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: theme.palette.grey["800"],
                  fontSize: "12px",
                  margin: "15px 0px",
                }}
              >
                <Box>
                  {pageType === "parentpathway"
                    ? t("parent_pathway_progress")
                    : t("pathway_progress")}
                </Box>
                <Box>{percentageComplete}%</Box>
              </Box>
              <BorderLinearProgress variant="determinate" value={percentageComplete} />
            </Box>
          )}
        {type === PathwayType.channel && (
          <Box
            display="flex"
            flexDirection={{ sm: "row" }}
            alignItems="center"
            justifyContent="flex-start"
            marginTop={{ xs: "10px" }}
            marginLeft={{ sm: "41px" }}
          >
            <Box marginRight={{ sm: "10px" }}>
              <label id="sort-by" style={{ fontSize: "10px" }}>
                {t("sort_by")}
              </label>
            </Box>
            <Box width={isMobile ? "100%" : "undefined"}>
              <Select
                title={t("sort_by")}
                aria-labelledby={"sort-by sort-by-select"}
                buttonLabelId={"sort-by"}
                id={"sort-by-select"}
                options={[
                  { title: t("sort_by_name_asc"), id: SORT_ALPHABETICAL_ASCENDING },
                  { title: t("sort_by_name_desc"), id: SORT_ALPHABETICAL_DESCENDING },
                  { title: t("sort_by_duration_asc"), id: SORT_DURATION_ASCENDING },
                  { title: t("sort_by_duration_desc"), id: SORT_DURATION_DESCENDING },
                  { title: t("sort_by_price_asc"), id: SORT_PRICE_ASCENDING },
                  { title: t("sort_by_price_desc"), id: SORT_PRICE_DESCENDING },
                ]}
                value={sort}
                onChange={handleSort}
                width={isMobile ? "100%" : "undefined"}
              />
            </Box>
          </Box>
        )}
        {type === PathwayType.playlist && (
          <Box
            display="flex"
            flexDirection={{ sm: "row" }}
            alignItems="center"
            justifyContent="flex-start"
            marginLeft={{ sm: "41px" }}
          >
            <Box marginRight={{ xs: 0, sm: "10px" }}>
              <label id="sort-by" style={{ fontSize: "10px" }}>
                {t("sort_by")}
              </label>
            </Box>
            <Box width={isMobile ? "100%" : "undefined"}>
              <Select
                title={t("sort_by")}
                buttonLabelId={"sort-by"}
                aria-labelledby={"sort-by sort-by-select"}
                id={"sort-by-select"}
                options={[
                  { title: t("sort_by_name_asc"), id: SORT_ALPHABETICAL_ASCENDING },
                  { title: t("sort_by_name_desc"), id: SORT_ALPHABETICAL_DESCENDING },
                  { title: t("sort_by_duration_asc"), id: SORT_DURATION_ASCENDING },
                  { title: t("sort_by_duration_desc"), id: SORT_DURATION_DESCENDING },
                  { title: t("sort_by_price_asc"), id: SORT_PRICE_ASCENDING },
                  { title: t("sort_by_price_desc"), id: SORT_PRICE_DESCENDING },
                ]}
                value={sort}
                onChange={handleSort}
                MenuProps={{
                  MenuListProps: {
                    "aria-labelledby": "sort-by",
                  },
                }}
                width={isMobile ? "100%" : "undefined"}
              />
            </Box>
          </Box>
        )}
      </Box>
      {showConfirmDialog && (
        <ConfirmDialog
          open={showConfirmDialog}
          title={confirmDialogTitle}
          message={confirmDialogMessage}
          handleConfirmed={handleConfirmDialogClick}
          handleClose={() => setConfirmDialog(false)}
        />
      )}
    </>
  );
};
